import React from "react";

import MoreHorizontal from "@tiny/icons/generated/MoreHorizontal";
import ChevronBigDown from "@tiny/icons/generated/ChevronBigDown";
import ChevronBigUp from "@tiny/icons/generated/ChevronBigUp";
import {
  Grid,
  Checkbox,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Link,
  TruncateTooltip,
  Chip,
  Box,
  OverFlowTip,
} from "@tiny/components";
import ItemActionTimeInfo from "@tiny/components/src/ItemActionTimeInfo/ItemActionTimeInfo";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { SuppressionView } from "@tiny/api";
import { useSuppressionListRow } from "./SuppressionListRow.hooks";
import {
  dateFormat,
  suppressionLabelMap,
  suppressionProcessingMap,
} from "./SuppressionListRow.helper";
import { SuppressionListRowProps } from "./SuppressionListRow.types";
import { useStyles } from "./SuppressionListRow.styles";
import { TinyImporter } from "../../TinyImportDialog";
import ToggleSuppressionTypeDialog from "./ToggleSuppressionTypeDialog";
import { formatNumber } from "@tiny/components/src/utils/formatUtils";
import TinySuppressionImporter from "../../AddSuppressionDialog/TinySuppressionImporter";
import SuppressionFilesDialog from "./SuppressionFilesDialog";

function SuppressionListRow(props: SuppressionListRowProps) {
  const { suppression } = props;

  const { mdUp } = useBreakpoints();

  const classes = useStyles();

  const {
    onSelect,
    subscribersText,
    disabledCheckbox,
    timeActions,
    sortAction,
    chipColor,
    chipText,
    moreActions,
    detailsOpen,
    moreButtonRef,
    checked,
    moreOpen,
    toggleDetails,
    showMore,
    hideMore,
    mutationLoading,
    audienceLocation,
    closeToggleSuppressionDialog,
    toggleSuppressionDialogOpen,
    downloadSuppressionDetails,
    isCheckboxChecked,
    isCheckboxDisabled,
    handleSelect,
    appendImporterOpen,
    closeAppendImporter,
    reloadSuppressionList,
    filesDialogOpen,
    closeFilesDialog,
    suppressionFiles,
  } = useSuppressionListRow(props);

  let suppressionName: React.ReactElement;
  if (mdUp) {
    suppressionName = (
      <TruncateTooltip arrow>
        <Box
          component="span"
          onClick={() => downloadSuppressionDetails(suppression.id || "")}
          style={{ cursor: "pointer" }}
        >
          <Typography
            variant="label1"
            mr={2}
            className={classes.name}
            noWrap
            color="primary"
            tone="main"
          >
            {suppression.name}
          </Typography>
        </Box>
      </TruncateTooltip>
    );
  } else {
    suppressionName = (
      <Box
        component="span"
        onClick={() => downloadSuppressionDetails(suppression.id || "")}
        style={{ cursor: "pointer" }}
      >
        <Typography
          variant="h4"
          mr={2}
          color="primary"
          tone="main"
          component="span"
        >
          {suppression.name && <OverFlowTip text={suppression.name} />}
        </Typography>
      </Box>
    );
  }

  return (
    <li className={classes.root}>
      <div className={classes.top}>
        <div className={classes.check}>
          <Checkbox
            color="primary"
            checked={isCheckboxChecked}
            onChangeValue={handleSelect}
            disabled={isCheckboxDisabled}
          />
        </div>
        <div className={classes.main}>
          {mdUp ? (
            <>
              <Link>{suppressionName}</Link>
              <Chip label={chipText} color={chipColor} size="small" />
              <ItemActionTimeInfo
                actions={timeActions}
                dateFormat={dateFormat}
                highlight={sortAction}
                display="inline"
              />
            </>
          ) : (
            <Box display="flex" flexDirection="column">
              <Box
                minWidth={0}
                display="flex"
                flexDirection="row"
                height={20}
                position="relative"
              >
                <Link>{suppressionName}</Link>
              </Box>
              <Box
                minWidth={0}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >
                <Chip label={chipText} color={chipColor} size="small" />
                <ItemActionTimeInfo
                  actions={timeActions}
                  dateFormat={dateFormat}
                  highlight={sortAction}
                  display="inline"
                />
                <IconButton
                  onClick={toggleDetails}
                  className={classes.detailsButton}
                  size="large"
                >
                  {detailsOpen ? <ChevronBigUp /> : <ChevronBigDown />}
                </IconButton>
              </Box>
            </Box>
          )}
        </div>
        {mdUp && (
          <div className={classes.details}>
            <Details item={suppression} subscribersText={subscribersText} />
          </div>
        )}
        <div className={classes.more}>
          {moreActions.length > 0 && (
            <IconButton
              ref={moreButtonRef}
              className={classes.moreButton}
              onClick={showMore}
              disabled={disabledCheckbox}
              size="large"
            >
              <MoreHorizontal />
            </IconButton>
          )}
          <Menu
            anchorEl={moreButtonRef.current}
            keepMounted
            open={moreOpen}
            onClose={hideMore}
            disableScrollLock
          >
            {moreActions.map((action) => (
              <MenuItem
                key={action.name}
                onClick={() => {
                  hideMore();
                  action.onClickAction(suppression.id!);
                }}
                disabled={mutationLoading}
              >
                {action.name}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
      {!mdUp && (
        <Collapse in={detailsOpen}>
          <div className={classes.bottom}>
            <Details item={suppression} subscribersText={subscribersText} />
          </div>
        </Collapse>
      )}
      <ToggleSuppressionTypeDialog
        open={toggleSuppressionDialogOpen}
        suppression={suppression}
        onClose={closeToggleSuppressionDialog}
        onSuccess={() => {
          reloadSuppressionList();
        }}
      />
      <TinySuppressionImporter
        open={appendImporterOpen}
        mode="append"
        suppressionId={suppression.id}
        suppressionType={suppression.type}
        onClose={closeAppendImporter}
        onSuccess={() => {
          closeAppendImporter();
          reloadSuppressionList();
        }}
      />
      <SuppressionFilesDialog
        open={filesDialogOpen}
        onClose={closeFilesDialog}
        files={suppressionFiles}
        suppressionId={suppression.id || ""}
      />
    </li>
  );
}

export interface DetailsProps {
  item: SuppressionView;
  subscribersText: string;
}

function Details(props: DetailsProps) {
  const { item, subscribersText } = props;
  return (
    <Grid container verticalSpacing={3}>
      <Grid item xs={6} md={4}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {formatNumber(item.suppressedCount)}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          Counts
        </Typography>
      </Grid>
      <Grid item xs={6} md={4}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {item?.type && suppressionLabelMap[item.type]}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          Type
        </Typography>
      </Grid>
      <Grid item xs={6} md={4}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {item?.fileProcessStatus &&
            suppressionProcessingMap[item?.fileProcessStatus]}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          Status
        </Typography>
      </Grid>
    </Grid>
  );
}

export default React.memo(SuppressionListRow);
